import { usePathname, useSearchParams } from 'next/navigation'
import router from 'next/router'
import { useCallback, useEffect, useMemo } from 'react'

import { TAbsoluteLocale } from '@src/config/i18n.config'
import { captureError } from '@src/quince-site-common/src/utils/sentryUtil'
import { noop } from '@src/utils/functions/utils'
import { TRegionDetail } from '@src/utils/i18n.utils'

import { checkRegionChanged, getUpdatedLocaleForURLAfterRegionSwitch } from '../utils'

const useRegionSwitchHandler = ({
  locale,
  regionDetails,
  showConfirmationModal,
  setLocale,
  isLocaleUpdating,
}: {
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
  showConfirmationModal: (locale: TAbsoluteLocale, callback: () => void) => void
  setLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => void | Promise<void>
  isLocaleUpdating: boolean
}) => {
  const { isRegionChange, targetLocale, isLanguageChangedInSameRegion } = useMemo(() => {
    return checkRegionChanged({
      locale,
      regionDetails,
    })
  }, [locale, regionDetails])

  const pathname = usePathname()
  const searchParams = useSearchParams()

  const handleLanguageSwitchInSameRegion = useCallback(async () => {
    try {
      await setLocale(targetLocale as TAbsoluteLocale, true)
      const updatedLocale = getUpdatedLocaleForURLAfterRegionSwitch(targetLocale as TAbsoluteLocale, regionDetails)
      const pathnameWithSearchParams = pathname + (searchParams.size > 0 ? '?' + searchParams.toString() : '')
      await router.replace(new URL(pathnameWithSearchParams, window.location.origin), undefined, {
        locale: updatedLocale,
      })
    } catch (error) {
      captureError(error)
    }
  }, [pathname, regionDetails, searchParams, setLocale, targetLocale])

  useEffect(() => {
    if (isLocaleUpdating) return
    if (isRegionChange) {
      showConfirmationModal(targetLocale as TAbsoluteLocale, noop)
    } else if (isLanguageChangedInSameRegion) {
      void handleLanguageSwitchInSameRegion()
    }
  }, [
    isLocaleUpdating,
    isRegionChange,
    targetLocale,
    showConfirmationModal,
    regionDetails,
    isLanguageChangedInSameRegion,
    handleLanguageSwitchInSameRegion,
  ])
}

export default useRegionSwitchHandler
